import { PRICING, WEB_DISCOUNT_PERCENT, applyWebDiscount } from '@/lib/auth/feature-flags'

/**
 * The same Premium subscription carries two prices.
 *
 * - **App Store price** — `PRICING`, held at parity with the RevenueCat
 *   products (`wgit_monthly_799` / `wgit_annual_4799`).
 * - **Web price** — lower by `WEB_DISCOUNT_PERCENT`, because both Stripe
 *   checkout routes attach the `STRIPE_WEB_DISCOUNT_COUPON_ID` coupon.
 *
 * A surface must quote whichever price its own CTA actually charges. A button
 * that opens Stripe checkout quotes `web`; a button that opens the App Store
 * quotes `appStore`. `/app` is the live example of the second case — its
 * pricing card sits directly above an `AppStoreCta`, so it quotes the App Store
 * price even though it is a web page.
 */

const toDollars = (cents: number) => cents / 100

function plan(listCents: number, interval: 'month' | 'year') {
  const webCents = applyWebDiscount(listCents)
  return {
    appStoreCents: listCents,
    webCents,
    /** Dollars, for existing `${...}` interpolation on the pricing surfaces. */
    appStore: toDollars(listCents),
    web: toDollars(webCents),
    appStoreLabel: `$${toDollars(listCents).toFixed(2)}`,
    webLabel: `$${toDollars(webCents).toFixed(2)}`,
    /**
     * Same shape as `PRICING.*.display`. Renewal copy on the Stripe surfaces
     * must use these rather than `PRICING.*.display`, or one page states two
     * different renewal prices.
     */
    appStoreDisplay: `$${toDollars(listCents).toFixed(2)}/${interval}`,
    webDisplay: `$${toDollars(webCents).toFixed(2)}/${interval}`,
  }
}

export const WEB_DISCOUNT = {
  percent: WEB_DISCOUNT_PERCENT,
  /** False when the offer is switched off, so surfaces drop the anchor copy. */
  active: WEB_DISCOUNT_PERCENT > 0,
  label: `${WEB_DISCOUNT_PERCENT}%`,
  monthly: plan(PRICING.MONTHLY.amount, 'month'),
  yearly: plan(PRICING.YEARLY.amount, 'year'),
} as const

/**
 * One sentence explaining why the web price is lower. Buyers read an unexplained
 * discount as a price that will rise later; naming the App Store fee makes it
 * read as a standing difference instead of a promotion.
 */
export const WEB_DISCOUNT_REASON = 'Buying direct skips the App Store fee, so we pass it back to you.'
